import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { Subject } from 'rxjs';

@Component({
  selector: 'bible-reference-range-form',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BibleReferenceRangeFormComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => BibleReferenceRangeFormComponent),
      multi: true,
    },
  ],
  templateUrl: './bible-reference-range-form.component.html',
  styleUrl: './bible-reference-range-form.component.css',
})
export class BibleReferenceRangeFormComponent<T extends Record<string, string>>
  implements ControlValueAccessor, OnInit, Validator, OnDestroy
{
  @Input() bibleEnum: Record<string, string> = {};
  @Output() onSelect = new EventEmitter<string>();
  @Input() verseRequired: boolean = false;
  @Input() required: boolean = true;

  innerForm: FormGroup;

  query: string = '';
  filteredBooks: string[] = [];
  showDropdown = false;
  activeIndex: number = -1;

  exit$: Subject<void> = new Subject<void>();

  constructor(private fb: FormBuilder) {
    this.innerForm = this.fb.group({
      book: ['', [Validators.required]],
      chapter: [1, [Validators.required, Validators.min(1)]],
      fromVerse: [null],
      toVerse: [null],
    });
  }

  ngOnInit(): void {
    if (!this.required) {
      this.innerForm.get('book')?.clearValidators();
      this.innerForm.get('book')?.updateValueAndValidity({ emitEvent: false });

      this.innerForm.get('chapter')?.clearValidators();
      this.innerForm.get('chapter')?.setValidators([Validators.min(1)]);
      this.innerForm.get('chapter')?.updateValueAndValidity({ emitEvent: false });
    }

    this.updateVerseValidators();

    // 1. Notify parent of ANY change in the inner form
    this.innerForm.valueChanges.subscribe((value) => {
      this.onChange(value);
    });

    // 2. Handle dependent logic for fromVerse -> toVerse
    this.innerForm.get('fromVerse')?.valueChanges.subscribe((from) => {
      const toControl = this.innerForm.get('toVerse');
      if (!toControl) return;

      if (from) {
        const maxAllowed = from + 9;

        // Update validators
        toControl.setValidators([
          Validators.min(from),
          Validators.max(maxAllowed),
        ]);

        // If the current "to" value is outside the new bounds, fix it SILENTLY
        if (toControl.value > maxAllowed || toControl.value < from) {
          toControl.setValue(maxAllowed, { emitEvent: false });
        }
      } else {
        toControl.clearValidators();
      }

      // Update validity so the error styling reflects the new validators
      toControl.updateValueAndValidity({ emitEvent: false });
    });
  }

  private updateVerseValidators() {
    const controls = [
      this.innerForm.get('fromVerse'),
      this.innerForm.get('toVerse'),
    ];

    controls.forEach((control) => {
      if (this.verseRequired) {
        control?.setValidators([Validators.required, Validators.min(1)]);
      } else {
        control?.clearValidators();
      }
      control?.updateValueAndValidity({ emitEvent: false });
    });
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  writeValue(value: any): void {
    if (value) {
      this.innerForm.setValue(value, { emitEvent: false });
    } else {
      this.innerForm.reset({}, { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.innerForm.disable() : this.innerForm.enable();
  }

  // --- Validator Interface ---
  // This allows the parent form to know if the internal fields are invalid
  validate(control: AbstractControl): ValidationErrors | null {
    if (!this.required && !this.innerForm.value.book) {
      return null;
    }
    return this.innerForm.valid ? null : { incompleteReference: true };
  }

  ngOnDestroy() {
    this.exit$.next();
    this.exit$.complete();
  }

  onBookInput() {
    const value = this.innerForm.get('book')?.value?.toLowerCase() || '';
    if (!value) {
      this.filteredBooks = [];
      this.showDropdown = false;
      this.activeIndex = -1; // Reset
      return;
    }

    this.filteredBooks = Object.values(this.bibleEnum ?? {}).filter((book) =>
      book.toLowerCase().includes(value),
    );

    this.showDropdown = true;
    this.activeIndex = 0; // Highlight the first item by default
  }

  handleBookKeyDown(event: KeyboardEvent) {
    if (!this.showDropdown || this.filteredBooks.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault(); // Stop cursor from moving
        this.activeIndex = (this.activeIndex + 1) % this.filteredBooks.length;
        break;

      case 'ArrowUp':
        event.preventDefault(); // Stop cursor from moving
        this.activeIndex =
          (this.activeIndex - 1 + this.filteredBooks.length) %
          this.filteredBooks.length;
        break;

      case 'Enter':
      case 'Tab':
        if (this.activeIndex >= 0) {
          event.preventDefault();
          this.selectBook(this.filteredBooks[this.activeIndex]);
        }
        break;

      case 'Escape':
        this.showDropdown = false;
        this.activeIndex = -1;
        break;
    }
  }

  selectBook(book: string) {
    this.innerForm.patchValue({ book: book });
    this.onChange(this.innerForm.value);
    this.showDropdown = false;
    this.filteredBooks = [];
    this.activeIndex = -1;
  }

  hideDropdown() {
    // Timeout allows the click event on the list to fire before it's removed from DOM
    setTimeout(() => (this.showDropdown = false), 200);
  }
}
